import React, {useEffect, useState} from 'react';
import {
    AccentWithDestructive,
    Button,
    ColumnDataType,
    DataTable,
    Spinner,
    SquareButton
} from "@tag/tag-components-react-v4";
import AppGroupService from "../services/AppGroupService";
import {AppGroup} from "../types/AppGroup";
import ServiceHealth from "./ServiceHealth";
import {ArrowTopLeftFilled, CircleCrossFilled, CircleTickFilled} from "@tag/tag-icons";
import {ServiceHealthDTO} from "../types/ServiceHealthDTO";
import moment from "moment";
import styles from './AppGroups.module.scss';
import { ProjectAccents } from "../../General/config/projectAccents";

const AppGroups = () => {    
    const [appGroups, setAppGroups] = useState<AppGroup[]>([]);
    const [loading, setLoading] = useState<boolean>();
    const [appGroupsHealthsSummary, setAppGroupsHealthsSummary] = useState<{appGrp: AppGroup, serviceHealths: ServiceHealthDTO[]}[]>([]);
    const [selectedAppGroupHealthSummary, setSelectedAppGroupHealthSummary] = useState<{appGrp: AppGroup, serviceHealths: ServiceHealthDTO[]}>();
    const hasMultipleCorporateSites = new Set(
        appGroups
            .map(appGroup => appGroup.corporateSiteName)
            .filter((siteName): siteName is string => !!siteName)
    ).size > 1;
    
    useEffect(() => {
        setLoading(true);
        AppGroupService.getAppGroups()
            .then((response) => {
                setAppGroups(response)
                setLoading(false);
            });

    }, []);

    useEffect(() => {
        const fetchServiceHealthsSequentially = async () => {
            for (const ag of appGroups) {
                try {
                    const serviceHealths = await AppGroupService.getCachedServiceHealthListByAppGroupName(
                        ag.name,
                        ag.corporateSiteName
                    );
                    setAppGroupsHealthsSummary((prevState) => [
                        ...prevState,
                        {
                            appGrp: ag,
                            serviceHealths: serviceHealths,
                        },
                    ]);
                } catch (error) {
                    // Handle error?
                }
            }
        };

        if (appGroups.length > 0) {
            fetchServiceHealthsSequentially();
        }
    }, [appGroups]);
    
    const getData = () => {
        let shaded = false;
        return appGroups.map((ag) => {
            shaded = !shaded;
            
            return {
                appGroup: ag.name,
                corporateSite: ag.corporateSiteName,
                totalServices: ag.totalServices,
                inUseServices: ag.assignedServices,
                availableServices: ag.unassignedServices,
                health: null,
                healthCheckedAt: null,
                shaded: shaded
            }
        });
    }

    const getColumns = () => {
        const columns = [
            { accessorKey: 'appGroup', header: 'App Group', meta: { dataType: 'string' as ColumnDataType } },
            { accessorKey: 'inUseServices', header: 'In Use', meta: { dataType: 'number' as ColumnDataType } },
            { accessorKey: 'availableServices', header: 'Available', meta: { dataType: 'number' as ColumnDataType } },
            { accessorKey: 'totalServices', header: 'Total Services', meta: { dataType: 'number' as ColumnDataType } },
            { accessorKey: 'healthCheckedAt', header: 'Health Checked At', cell: renderHealthCheckedDateTime },
            { accessorKey: 'health', header: 'Health', cell: renderHealthButton, },
        ]

        if (hasMultipleCorporateSites) {
            columns.unshift({
                accessorKey: 'corporateSite',
                header: 'Corporate Site',
                meta: { dataType: 'string' as ColumnDataType }
            });
        }

        return columns;
    }
    
    const renderHealthCheckedDateTime = ({row}: any) => {
        const result = appGroupsHealthsSummary.find(n =>
            n.appGrp.name === row.original.appGroup
            && n.appGrp.corporateSiteName === row.original.corporateSite
        );
        
        if (result) {
            const lastDatetime = result.serviceHealths[result.serviceHealths.length-1].checkedAt;
            return moment(lastDatetime).format('HH:mm - DD/MM/YYYY');
        }
    }
    
    const renderHealthButton = ({row}: any) => {
        const result = appGroupsHealthsSummary.find(n =>
            n.appGrp.name === row.original.appGroup
            && n.appGrp.corporateSiteName === row.original.corporateSite
        );
        if (!result) {
            return (
                <SquareButton
                    disabled
                >
                    <Spinner
                        wheelProps={{style: {height: '20px', width: '20px', backgroundSize: '20px'}}}
                    />
                </SquareButton>
            );
        }

        let accent: AccentWithDestructive|undefined = ProjectAccents.PeoplePlanner as AccentWithDestructive;
        let icon = <CircleTickFilled />;
            
        for (const aghs of result.serviceHealths) {
            const booleanValues = Object.values(aghs).filter(
                (value) => typeof value === 'boolean'
            );

            if (booleanValues.includes(false)) {
                accent = 'destructive';
                icon = <CircleCrossFilled />;
                break;
            }
        }
        
        return (
            <SquareButton
                accent={accent}
                icon={icon}
                onClick={() => {
                    setSelectedAppGroupHealthSummary(result)
                }}
            />
        )
    }

    let header = null;
    let content = null;
    if (loading) {
        content = <Spinner />;
    }
    else if (selectedAppGroupHealthSummary) {
        header = (
            <div style={{marginLeft: '2em'}}>
                <Button
                    className={styles.AppGroupsBackButton}
                    onClick={() => setSelectedAppGroupHealthSummary(undefined)}
                    icon={<ArrowTopLeftFilled />}
                />
                <h1 className={styles.AppGroupsHeader}>
                    {hasMultipleCorporateSites && selectedAppGroupHealthSummary.appGrp.corporateSiteName
                        ? `${selectedAppGroupHealthSummary.appGrp.corporateSiteName} — `
                        : ''}
                    {selectedAppGroupHealthSummary.appGrp.name} - Service Health
                </h1>
            </div>
        );
        content = 
            <ServiceHealth
                selectedAppGroupHealthSummary={selectedAppGroupHealthSummary}
            />;
    }
    else {
        content = (
            <DataTable
                scrollerProps={{
                    style:{ width: 'unset' } // Temporary while scroller not needed
                }}
                getRowProps={(row) => ({
                    style: {
                        backgroundColor: row.original.shaded ? '#FEFAEC' : '#F3F1FA'
                    },
                })}
                enableSorting={true}
                columns={getColumns()}
                data={getData()}
            />
        );
    }

    return (
        <>
            {header}
            {content}
        </>
    );
}

export default AppGroups;
